<script lang="ts">
    import { createEventDispatcher, onMount } from 'svelte'
    const dispatch = createEventDispatcher()
    import { faAngleLeft, faPlusCircle, faEdit, faSave, faTrashAlt } from '@fortawesome/free-solid-svg-icons'
    import Content from '../../_common/content.svelte'
    import LoadSpinner from '../../_common/load-spinner.svelte'
    import { Service } from '../../../modules/posApi/services/service'
    import type { CreateServiceDto } from '../../../modules/posApi/services/dto/create-service.dto'
    import type { UpdateServiceDto } from '../../../modules/posApi/services/dto/update-service.dto'
    import { GridButton, GridClass, GridCol, GridColTypeEnum, GridLine } from '../../_common/z/zgrid'
    import Zgrid from '../../_common/z/zgrid.svelte'
    import Zframe from '../../_common/z/zframe.svelte'
    import Zact from '../../_common/z/zact.svelte'
    import Zbutton from '../../_common/z/zbutton.svelte'
    import Zinput from '../../_common/z/zinput.svelte'
    import ZContainer from '../../_common/z/z-container.svelte'
    import ZContainerCol from '../../_common/z/z-container-col.svelte'
    import ZentitySelect from '../../_common/z/zentity-select.svelte'
    import ZinputBreak from '../../_common/z/zinput-break.svelte'

    // ##############################################################################################################

    const entityId = 'serviceId'

    export let form: boolean = false
    export let serviceId: string = null
    let entity: Service = new Service()

    const consumptionModeOptions = [
        { value: 'discrete', label: 'Integral (unidade inteira, ex: luva, touca)' },
        { value: 'fractional', label: 'Fracionado (ex: ml de shampoo, g de cera)' },
    ]
    const volumeUnitOptions = [
        { value: 'un', label: 'un' },
        { value: 'ml', label: 'ml' },
        { value: 'l', label: 'l' },
        { value: 'gotas', label: 'gotas' },
        { value: 'mg', label: 'mg' },
        { value: 'g', label: 'g' },
        { value: 'kg', label: 'kg' },
        { value: 'ui', label: 'ui' },
        { value: 'cm', label: 'cm' },
    ]

    function friendlyConsumptionMode(mode: string) {
        return consumptionModeOptions.find((el) => el.value === mode)?.label || mode
    }

    async function editEntity(id: string) {
        try {
            show = true
            entity = new Service()
            entity = await window.api.pos.services.getOne(id, { category: true })
            step = 2
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    async function newEntity() {
        entity = new Service()
        entity.consumptionMode = 'discrete'
        entity.deductFromAttendantComission = false
        step = 2
    }

    function buildDto(): CreateServiceDto {
        return {
            name: entity.name,
            description: entity.description || '',
            costs: entity.costs || 0,
            type: 'consumable',
            consumptionMode: entity.consumptionMode,
            nominalVolume: entity.consumptionMode === 'fractional' ? entity.nominalVolume || 0 : undefined,
            volumeUnit: entity.consumptionMode === 'fractional' ? entity.volumeUnit : undefined,
            deductFromAttendantComission: entity.deductFromAttendantComission || false,
            price: 0,
            duration: 0,
            consulting: false,
            balance: true,
            public: false,
            scheduleInterval: 0,
            schedulers: [],
            schedulersOnline: [],
            documents: [],
            needResource: false,
            comissionFixed: 0,
            comissionPercentage: 0,
            comissionCosts: 0,
            comissionFixedAttendant: 0,
            comissionPercentageAttendant: 0,
            comissionCostsAttendant: 0,
            expirationDays: undefined,
            blockDuplicity: false,
            serviceCategoryId: entity.category?.serviceCategoryId || entity.serviceCategoryId,
            minStock: entity.minStock,
        }
    }

    async function createEntity() {
        try {
            show = true
            entity = await window.api.pos.services.create(buildDto())
            if (form) {
                sendClose()
                return
            }
            await loadEntities()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    function sendClose() {
        dispatch('close')
    }

    async function updateEntity() {
        try {
            show = true
            const dto: UpdateServiceDto = buildDto()
            await window.api.pos.services.update(entity[entityId], dto)
            if (form) {
                sendClose()
                return
            }
            await loadEntities()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    async function saveEntity() {
        if (!entity.name) {
            evaluateError('Informe o nome do insumo')
            return
        }
        if (entity.consumptionMode === 'fractional' && !(entity.nominalVolume > 0)) {
            evaluateError('Informe o volume/quantidade nominal por embalagem (ex: 200 para um frasco de 200ml)')
            return
        }
        if (entity[entityId]) {
            await updateEntity()
        } else {
            await createEntity()
        }
    }

    async function loadEntities() {
        try {
            show = true
            step = 1
            entities = await window.api.pos.services.getAll({ type: 'consumable' })
            loadGrid()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    async function removeEntity() {
        if (!confirm('Deseja mesmo excluir este registro?')) {
            return
        }
        try {
            show = true
            await window.api.pos.services.remove(entity[entityId])
            await loadEntities()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    function evaluateError(error) {
        window.error(error)
    }

    function showEntities() {
        step = 1
    }

    let show = false
    let step = 1
    let entities: Service[] = []

    onMount(async () => {
        await loadEntities()
        if (form) {
            if (serviceId) {
                await editEntity(serviceId)
            } else {
                await newEntity()
            }
        }
    })

    let grid: GridClass = new GridClass()
    function loadGrid() {
        grid = new GridClass()
        grid.title = 'Insumos'
        grid.subtitle = ''
        grid.cols.push(new GridCol({ content: 'Insumo' }))
        grid.cols.push(new GridCol({ content: 'Alteração', type: GridColTypeEnum.datetime }))
        grid.cols.push(new GridCol({ content: 'Custo', size: 2, type: GridColTypeEnum.float_2 }))
        grid.cols.push(new GridCol({ content: 'Estoque', size: 1 }))
        grid.cols.push(new GridCol({ content: 'Consumo', size: 3 }))
        grid.cols.push(new GridCol({ content: '', type: GridColTypeEnum.button }))
        for (const item of entities) {
            const btnEdit = new GridButton({
                callback: () => editEntity(item.serviceId),
                icon: faEdit,
                label: 'Editar',
            })
            let line: GridLine = new GridLine()
            line.slots.push({ content: item.name })
            line.slots.push({ content: item.updatedAt })
            line.slots.push({ content: item.costs })
            line.slots.push({ content: item.stock })
            line.slots.push({ content: friendlyConsumptionMode(item.consumptionMode) })
            line.slots.push({ content: [btnEdit] })
            grid.lines.push(line)
        }
    }
</script>

<LoadSpinner {show} />
{#if !show}
    {#if step === 1}
        <Content footer={true}>
            <Zact>
                <div class="spacer" />
                {#if window.store.token.permissions.tools.services.write}
                    <Zbutton color="primary" icon={faPlusCircle} label="Novo insumo" mobileHideLabel={true} on:click={newEntity} />
                {/if}
            </Zact>
            <Zgrid class="h-space mb-7" {grid} />
        </Content>
    {/if}
    {#if step === 2}
        <Content>
            <Zact>
                {#if !form}
                    <Zbutton color="secondary" icon={faAngleLeft} label="Voltar" on:click={showEntities} />
                {/if}
                <div class="spacer" />
                {#if window.store.token.permissions.tools.services.write}
                    {#if !!entity[entityId]}
                        <Zbutton color="red" icon={faTrashAlt} label="Excluir" disabled={show} on:click={removeEntity} />
                    {/if}
                    <Zbutton color="primary" icon={faSave} label="Salvar" loading={show} disabled={show} on:click={saveEntity} />
                {/if}
            </Zact>

            <Zframe class="h-space" title="Insumo">
                <ZContainer>
                    <Zinput size={6} sizeM={12} label="Nome" bind:value={entity.name} />
                    <Zinput size={6} sizeM={12} label="Descrição" bind:value={entity.description} />

                    <ZContainerCol size={6} sizeM={12}>
                        <ZentitySelect fonte="categoriaServico" label="Categoria" bind:value={entity.category} />
                    </ZContainerCol>
                    <Zinput
                        size={6}
                        sizeM={12}
                        type="number"
                        label="Estoque mínimo (alerta)"
                        help="A dashboard de estoque sinaliza quando a quantidade em estoque chegar nesse valor ou menos. Deixe em branco pra não alertar."
                        bind:value={entity.minStock}
                    />
                </ZContainer>
            </Zframe>

            <Zframe class="mt-3" title="Consumo">
                <ZContainer>
                    <Zinput
                        size={6}
                        sizeM={12}
                        type="select"
                        label="Como este insumo é consumido"
                        options={consumptionModeOptions}
                        bind:value={entity.consumptionMode}
                    />
                    {#if entity.consumptionMode === 'fractional'}
                        <Zinput size={3} sizeM={6} type="number" label="Volume/quantidade nominal por unidade" bind:value={entity.nominalVolume} />
                        <Zinput size={3} sizeM={6} type="select" label="Unidade de medida" options={volumeUnitOptions} bind:value={entity.volumeUnit} />
                        <ZContainerCol size={12}>
                            <div class="text-muted">
                                Ex: se cada frasco fechado tem 200ml, informe 200 e "ml". Ao abrir um frasco na agenda, o atendente lança o quanto foi usado
                                (ex: 20ml por vez) até finalizar o frasco.
                            </div>
                        </ZContainerCol>
                    {/if}
                    <ZinputBreak></ZinputBreak>

                    <Zinput
                        size={6}
                        sizeM={12}
                        type="toggle"
                        skin="outline"
                        label="Descontar o custo do consumo da comissão do atendente"
                        bind:value={entity.deductFromAttendantComission}
                    />
                    <Zinput
                        disabled={entity.deductFromAttendantComission === false}
                        size={6}
                        sizeM={12}
                        type="number"
                        label="Custo unitário para fins de comissionamento {window.configs.currency}"
                        help="Caso o insumo seja de consumo fracionado o custo unitário é proporcional à quantidade utilizada. Caso seja integral o custo unitário é multiplicado pela quantidade informada no agendamento."
                        bind:value={entity.costs}
                    />
                </ZContainer>
            </Zframe>
        </Content>
    {/if}
{/if}

<style>
</style>
